import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { functionError } from '@/hooks/useMix'
import type { WatchState, Alert, WatchTypeId } from '@/components/alerts/types'
import { WATCH_TYPES } from '@/components/alerts/types'

/**
 * Breaking news: the watches, the alert budget and the alert queue (Brian's
 * settings-breaking.html v1.4, alerts.html v1.5, alert.html v1.4,
 * watch-empty.html v1.2). Everything writes through security definer
 * functions (migration 20240037); the tables are read-only to the app.
 */

// One realtime channel per account, however many screens listen (the tab bar's
// dot, the queue, the banner).
type Listener = (row: Alert, kind: string) => void
const live = { clientId: null as string | null, listeners: new Set<Listener>(), ch: null as ReturnType<typeof supabase.channel> | null }
function liveSubscribe(clientId: string, fn: Listener) {
  if (live.clientId !== clientId && live.ch) { supabase.removeChannel(live.ch); live.ch = null }
  live.clientId = clientId
  live.listeners.add(fn)
  if (!live.ch) {
    live.ch = supabase.channel(`gg-alerts-${clientId}`)
      .on('postgres_changes' as never, { event: '*', schema: 'public', table: 'alerts', filter: `client_id=eq.${clientId}` } as never,
        (p: { new: Alert; eventType: string }) => live.listeners.forEach((l) => l(p.new, p.eventType)))
      .subscribe()
  }
  return () => {
    live.listeners.delete(fn)
    if (!live.listeners.size && live.ch) { supabase.removeChannel(live.ch); live.ch = null; live.clientId = null }
  }
}
/** Calls fn for every change to this account's alerts (realtime). */
export function useAlertsLive(clientId: string | null | undefined, fn: Listener) {
  useEffect(() => {
    if (!clientId) return
    return liveSubscribe(clientId, (row, kind) => fn(row, kind))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])
}

const rpc = async <T>(fn: string, args?: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.rpc(fn as never, args as never)
  if (error) throw error
  return data as T
}

export function useWatchState() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['watch-state', clientId],
    enabled: !!clientId,
    staleTime: 15_000,
    queryFn: () => rpc<WatchState>('watch_state'),
  })
}

/** Every alert, newest first. Nothing expires; Earlier is the built and dismissed ones. */
export function useAlerts() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  useAlertsLive(clientId, () => {
    qc.invalidateQueries({ queryKey: ['alerts', clientId] })
    qc.invalidateQueries({ queryKey: ['watch-state', clientId] })
  })
  return useQuery({
    queryKey: ['alerts', clientId],
    enabled: !!clientId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('alerts' as never).select('*')
        .eq('client_id', clientId!).order('delivered_at', { ascending: false }).limit(300)
      if (error) throw error
      return (data ?? []) as unknown as Alert[]
    },
  })
}

function useInvalidate() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['watch-state', clientId] })
    qc.invalidateQueries({ queryKey: ['alerts', clientId] })
    qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    qc.invalidateQueries({ queryKey: ['seed-history', clientId] })
  }
}

/** Pause, auto top-up, the floor, push. */
export function useSetWatch() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: (patch: { paused?: boolean; auto?: boolean; floor?: number; push?: boolean }) => rpc('watch_set', { p_patch: patch }),
    onSuccess: done,
  })
}

/** + moves 5 in from this week's seeds; − moves 5 back. Resolves to the signed number moved. */
export function useMoveBudget() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (dir: 1 | -1) => rpc<number>('watch_move', { p_dir: dir }), onSuccess: done })
}

export function useAddWatch() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: (a: { type: WatchTypeId; subject: string; condition: string; own: boolean }) =>
      rpc<string>('watch_add', { p_type: a.type, p_subject: a.subject, p_condition: a.condition, p_own: a.own }),
    onSuccess: done,
  })
}
export function useRemoveWatch() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (id: string) => rpc('watch_remove', { p_id: id }), onSuccess: done })
}
export function useAddWatchType() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (type: WatchTypeId) => rpc('watch_type_add', { p_type: type }), onSuccess: done })
}
export function useRemoveWatchType() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (type: WatchTypeId) => rpc('watch_type_remove', { p_type: type }), onSuccess: done })
}

export function useDismissAlert() {
  const done = useInvalidate()
  return useMutation({ mutationFn: (id: string) => rpc('alert_dismiss', { p_id: id }), onSuccess: done })
}

/** Build, step one: the alert becomes a headline; the caller then runs the
 *  feed's build (useBuildStory), and the database marks the alert Built. */
export async function alertHeadline(id: string): Promise<string> {
  return rpc<string>('alert_headline', { p_id: id })
}

/** "Send a test alert" (Brian's test button): a sample match lands, 1 from
 *  the alert budget, and the push. Admins, or anyone on the local stack. */
export function useSendTestAlert() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('breaking-scan', { body: { test: true } })
      if (error) throw await functionError(error)
      return data as { ok: boolean; why?: string; bank?: number; cap?: number; topped?: number; last?: boolean; empty?: boolean }
    },
    onSuccess: done,
  })
}

/** Run the real matcher for this account now (admins, or the local stack). */
export function useScanNow() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('breaking-scan', { body: { scan: true } })
      if (error) throw await functionError(error)
      return data as { matched?: number; delivered?: number; skipped?: string; stopped?: string }
    },
    onSuccess: done,
  })
}

/** For the tab bar's Alerts dot: how many alerts are waiting (status new). */
export function useUnreadAlerts(): number {
  const { data } = useAlerts()
  return (data ?? []).filter((a) => a.status === 'new').length
}

const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`

/**
 * Feed controls' one-line summary for Breaking news (Brian's feed-controls.js):
 *   "3 watches on · safety alerts, snow & weather" · "Paused" · "None yet · add one"
 */
export function useBreakingSummary(): string | null {
  const { data: w } = useWatchState()
  if (!w) return null
  if (w.paused) return 'Paused'
  const n = w.watches.length
  if (!n) return 'None yet · add one'
  const names = w.types.filter((t) => w.watches.some((x) => x.type === t)).map((t) => WATCH_TYPES[t as WatchTypeId]?.name ?? t)
  return `${plural(n, 'watch', 'watches')} on · ${names.slice(0, 2).join(', ').toLowerCase()}`
}
